import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Menu,
  // ShoppingCart,
  LogOut,
  Globe,
  X,
  Settings,
  Package,
  Home,
  Phone,
  Info,
  // Wrench,
  // Calendar,
  Leaf,
  Briefcase,
  Layers,
  UserPlus,
  // HeartHandshake
} from 'lucide-react'
import { Button } from '../ui/button'
import broxelLogo from '../../assets/images/BROXEL-LOGO.png'
import Pharma4LinkMark from '../brand/Pharma4LinkMark'
import { useBrand } from '../../lib/brand'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
// import { Badge } from '../ui/badge'
import { useAuthStore, useCartStore, useLanguageStore } from '../../store'
import { useSupplierStore } from '../../store/supplierStore'
import { ctaDestination } from '../../lib/authDestination'
import { motion, AnimatePresence } from 'framer-motion'

const Header: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const { supplierUser, isSupplierAuthenticated, clearSupplierAuth } = useSupplierStore()

  // The header used to read the CUSTOMER store alone, so a signed-in partner
  // was offered Login and Register - an invitation to create an account they
  // already have. The site runs three independent sessions; this resolves the
  // one the account menu should speak for.
  //
  // The customer session wins a tie for the same reason lib/authDestination
  // gives it precedence: the avatar here and the hero's CTA sit on the same
  // page and must not disagree about who is looking at it.
  const partnerOnly = !user && isSupplierAuthenticated
  const account = user ?? (partnerOnly ? supplierUser : null)
  const accountHome = ctaDestination({
    customerRole: user?.role ?? null,
    partnerSignedIn: isSupplierAuthenticated
  })
  const accountProfile = partnerOnly ? '/supplier/profile' : '/dashboard/profile'
  const { getTotalItems } = useCartStore()
  const { language, setLanguage } = useLanguageStore()
  const brand = useBrand()
  const isPlatform = brand === 'pharma4link'

  const totalCartItems = getTotalItems()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [previousCartCount, setPreviousCartCount] = useState(totalCartItems)
  // const [cartAnimationKey, setCartAnimationKey] = useState(0)

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  // Track cart changes for animation
  useEffect(() => {
    if (totalCartItems > previousCartCount) {
      // Cart item count increased - trigger animation
      // setCartAnimationKey(prev => prev + 1)
    }
    setPreviousCartCount(totalCartItems)
  }, [totalCartItems, previousCartCount])

  // Navigation items with icons
  const navigationItems = [
    { label: t('nav.home'), path: '/', icon: Home },
    // Parked 2026-08-29 along with the /services routes in App.tsx.
    // { label: t('nav.services'), path: '/services', icon: Wrench },
    { label: t('nav.eqs'), path: '/eqs', icon: Layers },
    // 'nav.marketplace' reads "Pharma4Link" - the key names the route, not the label.
    { label: t('nav.marketplace'), path: '/marketplace', icon: Package },
    // 'nav.sdg' reads "Sustainability" - the key names the route, not the label.
    { label: t('nav.sdg'), path: '/sdg', icon: Leaf },
    { label: t('nav.projects'), path: '/projects', icon: Briefcase },
    // Parked 2026-08-07 along with the /partners route in App.tsx.
    // { label: t('nav.partners'), path: '/partners', icon: HeartHandshake },
    { label: t('nav.about'), path: '/about', icon: Info },
    // Hidden 2026-09-01 at the client's request. The /events route, page and both
    // locale keys are untouched - the page is live, just not linked from here.
    // { label: t('nav.events'), path: '/events', icon: Calendar },
    // Restored 2026-08-28. Dropped as collateral in 9e16017 ("removed unused
    // announcement nav"); the /announcements route, page and both locale keys
    // never went away, so the page was live but reachable only by typing the URL.
    // 'nav.announcements' reads "Jobs".
    { label: t('nav.announcements'), path: '/announcements', icon: UserPlus },
    { label: t('nav.contact'), path: '/contact', icon: Phone },
  ]
  
  const handleLanguageChange = (newLanguage: 'en' | 'ar') => {
    setLanguage(newLanguage)
    i18n.changeLanguage(newLanguage)
    
    // Update document direction for RTL support
    if (newLanguage === 'ar') {
      document.documentElement.dir = 'rtl'
      document.documentElement.lang = 'ar'
    } else {
      document.documentElement.dir = 'ltr'
      document.documentElement.lang = 'en'
    }
  }
  
  // Each session is logged out on its own. With BOTH signed in, the menu is
  // speaking for the customer, so this clears that one and the partner session
  // survives - the header then falls through to showing the partner. That is
  // the pre-existing behaviour (a customer logout has never touched the partner
  // store) and it is the honest one: they are two separate accounts.
  const handleLogout = () => {
    if (partnerOnly) {
      clearSupplierAuth()
      navigate('/supplier/login')
      return
    }
    logout()
    navigate('/')
  }
  
  return (
    <>
      <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled 
          ? 'bg-broxel-light-green/95 backdrop-blur-md shadow-lg border-b border-broxel-secondary/20' 
          : 'bg-broxel-light-green backdrop-blur-sm border-b border-broxel-secondary/10'
      }`}>
        <div className="container mx-auto px-4">
          {/* The Pharma4Link artwork is near-square (viewBox 421.6 x 392.4), so at any
              width that keeps its wordmark legible it is ~0.93x as tall and does not fit
              the Broxel row — it overflowed the header and clipped its own wordmark. The
              platform brand gets a taller row rather than an unreadably small mark. */}
          <div className={`flex items-center justify-between transition-all duration-300 ${
            isPlatform
              ? (isScrolled ? 'h-16' : 'h-20')
              : (isScrolled ? 'h-14' : 'h-16')
          }`}>
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group">
              {isPlatform ? (
                /* Pharma4Link alone — the "empowered by Broxel EQS" endorsement stays in
                   the Footer and on /login, where there is room for it to read. */
                <Pharma4LinkMark
                  width={isScrolled ? 60 : 76}
                  className="transition-all duration-300 group-hover:scale-105"
                />
              ) : (
                <img
                  src={broxelLogo}
                  alt="Broxel Pharmaceuticals"
                  className={`w-auto transition-all duration-300 group-hover:scale-105 ${
                    isScrolled ? 'h-11' : 'h-12'
                  }`}
                />
              )}
            </Link>
          
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.path
                return (
                  <Link 
                    key={item.path}
                    to={item.path} 
                    className={`relative text-sm font-medium transition-all duration-300 hover:text-blue-600 group ${
                      isActive 
                        ? 'text-blue-600' 
                        : 'text-gray-700 hover:text-blue-600'
                    }`}
                  >
                    {item.label}
                    {isActive && (
                      <motion.div
                        className="absolute -bottom-2 left-0 right-0 h-0.5 bg-blue-600 rounded-full"
                        layoutId="navbar-indicator"
                        initial={false}
                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                      />
                    )}
                    <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-blue-600 rounded-full scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                  </Link>
                )
              })}
            </nav>
          
            {/* Right Side Actions */}
            <div className="flex items-center space-x-3">
              {/* Language Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-9 w-9 p-0 hover:bg-gray-100 rounded-full relative"
                  >
                    <Globe className="h-4 w-4" />
                    <span className="sr-only">{t('header.changeLanguage')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[150px]">
                  <DropdownMenuItem 
                    onClick={() => handleLanguageChange('en')}
                    className={`${language === 'en' ? 'bg-blue-50 text-blue-600' : ''} cursor-pointer`}
                  >
                    <span className="mr-2">US</span>
                    English
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleLanguageChange('ar')}
                    className={`${language === 'ar' ? 'bg-blue-50 text-blue-600' : ''} cursor-pointer`}
                  >
                    <span className="mr-2">AR</span>
                    العربية
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            
              {/* Shopping Cart */}
              {/* <Button 
                variant="ghost" 
                size="sm" 
                className="relative h-9 w-9 p-0 hover:bg-gray-100 rounded-full group"
                onClick={() => navigate('/cart')}
              >
                <ShoppingCart className="h-4 w-4 group-hover:scale-110 transition-transform" />
                {totalCartItems > 0 && (
                  <motion.div
                    key={cartAnimationKey}
                    initial={{ scale: 1 }}
                    animate={{ 
                      scale: [1, 1.3, 1],
                      rotate: [0, 10, -10, 0]
                    }}
                    transition={{ 
                      duration: 0.6,
                      ease: "easeInOut"
                    }}
                  >
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center animate-bounce"
                    >
                      {totalCartItems}
                    </Badge>
                  </motion.div>
                )}
                <span className="sr-only">{t('header.cart', { count: totalCartItems })}</span>
              </Button> */}
            
              {/* User Menu */}
              {account ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-9 w-9 p-0 hover:bg-gray-100 rounded-full group"
                    >
                      <div className="h-7 w-7 rounded-full bg-gradient-brand flex items-center justify-center text-white text-sm font-medium group-hover:scale-105 transition-transform">
                        {account.first_name ? account.first_name.charAt(0).toUpperCase() : 'U'}
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2 border-b">
                      <p className="text-sm font-medium text-gray-900">
                        {account.first_name} {account.last_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {account.email}
                      </p>
                    </div>
                    <DropdownMenuItem onClick={() => navigate(accountHome)} className="cursor-pointer">
                      <Home className="mr-2 h-4 w-4" />
                      {t('nav.dashboard')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(accountProfile)} className="cursor-pointer">
                      <Settings className="mr-2 h-4 w-4" />
                      {t('header.profile')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600">
                      <LogOut className="mr-2 h-4 w-4" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="hidden md:flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/login')}
                    className="bg-white hover:bg-gray-50 border-gray-300"
                  >
                    {t('nav.login')}
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => navigate('/login?mode=signup')}
                    className="bg-gradient-brand hover:bg-gradient-brand-hover text-white border-0"
                  >
                    {t('nav.register')}
                  </Button>
                </div>
              )}
            
              {/* Mobile Menu Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(true)}
                className="lg:hidden h-9 w-9 p-0 hover:bg-gray-100 rounded-full"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">{t('header.openMenu')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/50 z-50 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 400 }}
              className="fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 lg:hidden overflow-y-auto"
            >
              {/* Mobile Menu Header */}
              <div className="flex items-center justify-between p-6 border-b">
                {isPlatform ? (
                  <Pharma4LinkMark width={72} />
                ) : (
                  <img
                    src={broxelLogo}
                    alt="Broxel Pharmaceuticals"
                    className="h-8 w-auto"
                  />
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="h-8 w-8 p-0 hover:bg-gray-100 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Mobile Navigation */}
              <nav className="py-6">
                {navigationItems.map((item) => {
                  const isActive = location.pathname === item.path
                  const IconComponent = item.icon
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      // This panel is pinned right-0 in both directions, so the accent bar
                      // goes on the edge the row *ends* on — right in LTR, left in RTL —
                      // which is also the edge facing the page. Unmirrored it sat against
                      // the screen edge in Arabic.
                      className={`flex items-center px-6 py-4 text-base font-medium transition-all hover:bg-gray-50 ${
                        isActive
                          ? `text-blue-600 bg-blue-50 border-blue-600 ${
                              language === 'ar' ? 'border-l-4' : 'border-r-4'
                            }`
                          : 'text-gray-700'
                      }`}
                    >
                      {IconComponent && <IconComponent className="mr-3 h-5 w-5" />}
                      {item.label}
                    </Link>
                  )
                })}
              </nav>

              {/* Mobile User Section */}
              <div className="border-t p-6">
                {account ? (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-full bg-gradient-brand flex items-center justify-center text-white font-medium">
                        {account.first_name ? account.first_name.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {account.first_name} {account.last_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {account.email}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          navigate(accountHome)
                          setIsMobileMenuOpen(false)
                        }}
                      >
                        <Home className="mr-2 h-4 w-4" />
                        {t('nav.dashboard')}
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          navigate(accountProfile)
                          setIsMobileMenuOpen(false)
                        }}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        {t('header.profile')}
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-red-600 hover:text-red-600 hover:bg-red-50"
                        onClick={handleLogout}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        {t('nav.logout')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        navigate('/login')
                        setIsMobileMenuOpen(false)
                      }}
                    >
                      {t('nav.login')}
                    </Button>
                    <Button
                      className="w-full bg-gradient-brand"
                      onClick={() => {
                        navigate('/login?mode=signup')
                        setIsMobileMenuOpen(false)
                      }}
                    >
                      {t('nav.register')}
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

export default Header
